import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

/** App background per theme (matches body `hsl(var(--bg))`) — used to tint the
 *  native status-bar / safe-area strips so they track light/dark. */
const NATIVE_BG = { dark: "#11161d", light: "#eff2f5" } as const;

type ThemeMode = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  /** User-selected mode (may be "system"). */
  theme: ThemeMode;
  /** What is actually applied right now ("light" | "dark"). */
  resolvedTheme: ResolvedTheme;
  setTheme: (mode: ThemeMode) => void;
  /** Quick toggle between light <-> dark, ignoring "system". */
  toggleTheme: () => void;
}

const STORAGE_KEY = "theme";
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

interface ThemeProviderProps {
  children: ReactNode;
  /** Default mode if nothing stored. */
  defaultTheme?: ThemeMode;
}

export const ThemeProvider = ({
  children,
  defaultTheme = "system",
}: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof window === "undefined") return defaultTheme;
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
    return defaultTheme;
  });

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  // Track OS-level changes when in "system" mode.
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemTheme(mql.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === "system" ? systemTheme : theme;

  // Apply to <html data-theme> + persist.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", resolvedTheme);
    // Help mobile browsers paint URL bar correctly.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute(
        "content",
        resolvedTheme === "dark" ? "#0E141C" : "#FAFBFC"
      );
    }
    // Native: tint the status-bar strip + icon style to the app theme so the
    // safe-area strips track light/dark (the capacitor.config value only sets
    // the first-paint color). Best-effort — no-op on web / if the plugin is
    // unavailable.
    if (Capacitor.isNativePlatform()) {
      // iOS runs edge-to-edge: overlay the status bar so the web view fills the
      // screen (contentInset "never") and env(safe-area-inset-top) reports the
      // real notch for the header inset. Android keeps its opaque status bar
      // (overlay off, from capacitor.config) — its layout already works.
      if (Capacitor.getPlatform() === "ios") {
        void StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
      }
      void StatusBar.setBackgroundColor({ color: NATIVE_BG[resolvedTheme] }).catch(() => {});
      void StatusBar.setStyle({ style: resolvedTheme === "dark" ? Style.Dark : Style.Light }).catch(() => {});
    }
  }, [resolvedTheme]);

  const setTheme = useCallback((mode: ThemeMode) => {
    localStorage.setItem(STORAGE_KEY, mode);
    setThemeState(mode);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === "light" ? "dark" : "light");
  }, [resolvedTheme, setTheme]);

  const value = useMemo<ThemeContextType>(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
};
